import React, { useState, useRef, useEffect } from "react";
import { FaCaretLeft, FaCaretRight } from "react-icons/fa";
import "../RegistrationForm/Pages.css";

const Calender = ({ value, onChange, placeholder, disableFuture = false, disablePast = false }) => {
  const wrapperRef = useRef(null);
  const today = new Date();
  const normalize = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(value ? value.getDate() : null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [isEditingYear, setIsEditingYear] = useState(false);
  const [yearDraft, setYearDraft] = useState(String(today.getFullYear()));
  const yearInputRef = useRef(null);

  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const handleDateClick = (day) => {
    const fullDate = new Date(currentYear, currentMonth, day);
    const normalized = normalize(fullDate);

    if (
      (!disableFuture || normalized <= normalize(today)) &&
      (!disablePast || normalized >= normalize(today))
    ) {
      onChange(fullDate);
      setSelectedDate(day);
      setShowCalendar(false);
    }
  };

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setShowCalendar(false);
      setIsEditingYear(false);
    }
  };

  const handleYearConfirm = () => {
    setIsEditingYear(false);
    if (!yearDraft || !/^\d{4}$/.test(yearDraft)) {
      setYearDraft(String(currentYear));
      return;
    }

    const nextYear = Number(yearDraft);
    if (!nextYear || Number.isNaN(nextYear)) {
      setYearDraft(String(currentYear));
      return;
    }

    setCurrentYear(nextYear);

    const fallbackDay = selectedDate ?? today.getDate();
    const fallbackMonth = currentMonth ?? today.getMonth();
    const newDate = new Date(nextYear, fallbackMonth, fallbackDay);
    const normalized = normalize(newDate);

    if (
      (!disableFuture || normalized <= normalize(today)) &&
      (!disablePast || normalized >= normalize(today))
    ) {
      onChange(newDate);
      setSelectedDate(fallbackDay);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isEditingYear && yearInputRef.current) {
      yearInputRef.current.focus();
    }
  }, [isEditingYear]);

  const openCalendar = () => {
    const baseDate = value || today;
    setCurrentMonth(baseDate.getMonth());
    setCurrentYear(baseDate.getFullYear());
    setYearDraft(String(baseDate.getFullYear()));
    setSelectedDate(baseDate.getDate());
    setShowCalendar(true);
  };

  const clearDate = (event) => {
    event.stopPropagation();
    onChange(null);
    setSelectedDate(null);
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    setYearDraft(String(today.getFullYear()));
    setShowCalendar(false);
    setIsEditingYear(false);
  };

  const canNavigatePrev = () => {
    if (!disablePast) return true;
    if (currentYear > today.getFullYear()) return true;
    if (currentYear === today.getFullYear() && currentMonth > today.getMonth()) return true;
    return false;
  };

  const canNavigateNext = () => {
    if (!disableFuture) return true;
    if (currentYear < today.getFullYear()) return true;
    if (currentYear === today.getFullYear() && currentMonth < today.getMonth()) return true;
    return false;
  };

  const prevMonth = () => {
    if (!canNavigatePrev()) return;
    const prev = currentMonth - 1;
    if (prev < 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth(prev);
    }
  };

  const nextMonth = () => {
    if (!canNavigateNext()) return;
    const next = currentMonth + 1;
    if (next > 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth(next);
    }
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div className="w-full cursor-pointer flex items-center justify-between gap-2" onClick={openCalendar}>
        <span className={value ? "text-white" : "text-[#949494]"}>
          {value
            ? `${String(value.getDate()).padStart(2, "0")}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getFullYear())}`
            : placeholder}
        </span>
        {value && (
          <button
            type="button"
            onClick={clearDate}
            className="bg-transparent border-none text-[#949494] cursor-pointer"
            aria-label="Clear date"
          >
            x
          </button>
        )}
      </div>

      {showCalendar && (
        <div className="absolute mt-3 w-full max-w-sm md:max-w-md lg:max-w-lg bg-white px-4 py-6 z-50">
          <div className="flex justify-between text-black mb-6 px-4">
            <div className="font-langit tracking-wider text-[18px] sm:text-[20px] md:text-[22px]">
              {monthNames[currentMonth]}{" "}
              {isEditingYear ? (
                <input
                  ref={yearInputRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  className="w-[80px] bg-transparent focus:outline-none"
                  value={yearDraft}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/\D/g, "").slice(0, 4);
                    setYearDraft(cleaned);
                  }}
                  onBlur={handleYearConfirm}
                  onKeyDown={(e) => { if (e.key === "Enter") handleYearConfirm(); }}
                />
              ) : (
                <span
                  onClick={() => {
                    setYearDraft(String(currentYear));
                    setIsEditingYear(true);
                  }}
                  className="cursor-pointer"
                >
                  {currentYear}
                </span>
              )}
            </div>

            <div className="flex items-center gap-0">
              <button type="button" className="bg-transparent border-none" onClick={prevMonth} disabled={!canNavigatePrev()}>
                <FaCaretLeft size={24} className={`text-[#808080] ${!canNavigatePrev() ? "opacity-40" : ""}`} />
              </button>
              <button type="button" className="bg-transparent border-none" onClick={nextMonth} disabled={!canNavigateNext()}>
                <FaCaretRight size={24} className={`text-[#808080] ${!canNavigateNext() ? "opacity-40" : ""}`} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 text-center font-semibold text-[#787878] mb-2 text-xs sm:text-base justify-items-center">
            {daysOfWeek.map((day) => (
              <div key={day} className="w-9 h-9 flex items-center justify-center">{day}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 text-black gap-y-2 px-2 text-xs sm:text-base justify-items-center">
            {Array(getFirstDayOfMonth(currentMonth, currentYear)).fill("").map((_, i) => (
              <div key={`empty-${i}`} className="w-9 h-9" />
            ))}

            {Array.from({ length: getDaysInMonth(currentMonth, currentYear) }, (_, i) => i + 1).map((day) => {
              const date = new Date(currentYear, currentMonth, day);
              const normalized = normalize(date);
              const isDisabled =
                (disableFuture && normalized > normalize(today)) ||
                (disablePast && normalized < normalize(today));
              return (
                <div
                  key={day}
                  onClick={() => !isDisabled && handleDateClick(day)}
                  className={`cursor-pointer w-7 md:w-9 h-7 md:h-9 flex items-center justify-center 
                    ${selectedDate === day ? "selected-border font-bold" : ""}
                    ${isDisabled ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-200"}`}
                >
                  {day}
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={() => setShowCalendar(false)}
              className="bg-[#D3631D] text-white border-none font-bold px-8 py-2 rounded-full text-sm sm:text-base"
            >
              DONE
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calender;